<template>
  <div class="px-10 py-5">
    <v-card elevation="0">
      <div class="d-flex align-center justify-space-between mb-6">
        <h3
          class="text--black text-uppercase"
          style="font-size: 18px; font-weight: 500"
        >
          Grafik Pendapatan
        </h3>
        <div class="d-flex">
          <v-select
            outlined
            multiple
            hide-details
            v-model="selectedGate"
            :items="gateItems"
            item-text="name"
            item-value="code"
            label="Gerbang Tol"
            class="mr-4 rounded-lg"
            :menu-props="{
              location: 'bottom',
              origin: 'top center',
              transition: 'slide-y-transition',
              offsetY: true,
            }"
          >
            <template v-slot:selection="{ index }">
              <span class="mr-3" style="font-size: 14px" v-if="index < 1"
                >Gerbang Tol</span
              >
              <div
                v-if="index === 0"
                class="circle-chip"
                :style="{
                  fontSize: '14px',
                  border: '1px solid #E16162',
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }"
              >
                {{ selectedGate.length }}
              </div>
            </template>
            <template v-slot:item="{ item }">
              <span style="font-size: 14px">{{ item.name }}</span>
            </template>
          </v-select>

          <v-select
            outlined
            v-model="selectedPeriod"
            :items="periodItems"
            item-text="title"
            item-value="value"
            label="Periode"
            hide-details
            class="rounded-lg"
            :menu-props="{
              location: 'bottom',
              origin: 'top center',
              transition: 'slide-y-transition',
              offsetY: true,
            }"
          >
            <template v-slot:selection="{ item }">
              <span style="font-size: 14px">{{ item.title }}</span>
            </template>
            <template v-slot:item="{ item }">
              <span style="font-size: 14px">{{ item.title }}</span>
            </template>
          </v-select>

          <v-select
            outlined
            v-model="selectedAdditionalInfo"
            :items="[
              { title: 'Real', value: 'average' },
              { title: 'Rekonsiliasi', value: 'reconcile' },
            ]"
            item-text="title"
            item-value="value"
            label="Info"
            hide-details
            class="rounded-lg ml-3"
            :menu-props="{
              location: 'bottom',
              origin: 'top center',
              transition: 'slide-y-transition',
              offsetY: true,
            }"
          >
          </v-select>
        </div>
      </div>

      <!-- Chart Container -->
      <div
        class="py-6 chart-container"
        style="position: relative; height: 400px"
      >
        <canvas ref="revenueChart"></canvas>
      </div>
    </v-card>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import * as constant from "@/constants/constant";

export default {
  props: {
    data: {
      type: Array,
      required: true,
    },
    averageReconcile: {
      type: Number,
      default: 0,
    },
    average: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      gateItems: [],
      periodItems: constant.periods,
      selectedGate: [],
      selectedPeriod: "daily",
      chart: null,
      selectedAdditionalInfo: "average",
    };
  },
  watch: {
    selectedGate() {
      this.emitDateUpdates();
    },
    selectedPeriod() {
      this.emitDateUpdates();
    },
    data() {
      this.updateChart();
    },
    selectedAdditionalInfo() {
      this.updateChart();
    },
  },
  computed: {
    processedData() {
      if (!this.data || this.data.length === 0) {
        return { labels: [], datasets: [] };
      }

      const labels = this.data.map((item) => {
        const date = new Date(item.date);
        const monthName = date.toLocaleString("default", { month: "short" });
        return `${date.getDate()} ${monthName}`;
      });

      const datasets = [
        {
          label: "Total Pendapatan",
          data: this.data.map((item) =>
            this.selectedAdditionalInfo === "average"
              ? Number(item.total)
              : Number(item.total_reconcile)
          ),
          backgroundColor: "#ABD1C6",
          borderColor: "#ABD1C6",
          borderWidth: 1,
          type: "bar",
          order: 2, // Bar drawn first (back)
        },
      ];

      // Add horizontal line
      const horizontalLineValue =
        this.selectedAdditionalInfo === "average"
          ? this.average
          : this.averageReconcile;
      const horizontalLineLabel =
        this.selectedAdditionalInfo === "average"
          ? "Rerata"
          : "Rerata Rekonsiliasi";

      datasets.push({
        label: horizontalLineLabel,
        data: Array(this.data.length).fill(horizontalLineValue),
        borderColor: "#000000",
        borderWidth: 2,
        type: "line",
        pointRadius: 0,
        order: 1, // Line drawn last (front)
      });

      return { labels, datasets };
    },
  },
  async mounted() {
    await this.fetchDataGate();
    this.initChart();
    this.emitDateUpdates();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
  methods: {
    async fetchDataGate() {
      try {
        const params = { is_has_income: true };
        const response = await this.$axios.get("gates", { params });
        this.gateItems = response.data;
        this.selectedGate = this.gateItems.map((item) => item.code);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    emitDateUpdates() {
      this.$emit("update:selectedGate", this.selectedGate);
      this.$emit("update:selectedPeriod", this.selectedPeriod);
    },
    initChart() {
      if (this.chart) {
        this.chart.destroy();
      }

      const ctx = this.$refs.revenueChart.getContext("2d");
      this.chart = new Chart(ctx, {
        type: "bar",
        data: this.processedData || {
          labels: [],
          datasets: [],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            x: {
              grid: {
                display: true,
                color: "#f0f0f0",
              },
            },
            y: {
              grid: {
                display: true,
                color: "#f0f0f0",
              },
              ticks: {
                callback: function (value) {
                  return value.toLocaleString();
                },
              },
              suggestedMin: 0,
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label) {
                    label += ": ";
                  }
                  if (context.parsed.y !== null) {
                    label += new Intl.NumberFormat("id-ID", {
                      style: "currency",
                      currency: "IDR",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(context.parsed.y);
                  }
                  return label;
                },
              },
            },
          },
        },
      });
    },
    updateChart() {
      if (this.chart) {
        this.chart.data = this.processedData;
        this.chart.update();
      }
    },
  },
};
</script>

<style scoped>
.v-select {
  max-width: 200px;
}

.circle-chip {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  text-align: center;
  line-height: 40px;
  background-color: #e16162;
}
</style>
